// apps/widget/src/components/phone-field.tsx
//
// A phone number with its country: flag and dialling code on the left, the
// number on the right, checked against that country's numbering plan.
//
// The country picker is the platform's own <select> under a styled face — on a
// phone that opens the native wheel or sheet, which beats any custom list for
// two hundred countries. The validation library is loaded with the field, not
// with the widget: most visitors never reach a form.
//
// What leaves is E.164 (+447700900123), so the clinic can dial or WhatsApp it
// without guessing which country a local number belongs to.

import { useEffect, useId, useMemo, useState } from 'react';
import type { CountryCode } from 'libphonenumber-js/min';
import { getLocale, t } from '../lib/i18n';

type Lib = typeof import('libphonenumber-js/min');

let lib: Lib | null = null;
let loading: Promise<Lib> | null = null;

function loadLib(): Promise<Lib> {
  loading ??= import('libphonenumber-js/min').then((m) => (lib = m));
  return loading;
}

export interface PhoneValue {
  country: CountryCode;
  number: string;
}

/** The number in E.164 when it is valid for its country, otherwise null. */
export function phoneE164(value: PhoneValue): string | null {
  if (!lib) return null;
  const parsed = lib.parsePhoneNumberFromString(value.number, value.country);
  return parsed?.isValid() ? parsed.number : null;
}

/** The country to open on: the clinic's, else the visitor's browser region, else GB. */
export function defaultCountry(clinicCountry: string | null | undefined): CountryCode {
  const fromBrowser = navigator.language.split('-')[1];
  return (clinicCountry ?? fromBrowser ?? 'GB').toUpperCase() as CountryCode;
}

/**
 * A round flag, served by the widget itself (circle-flags, copied in at build
 * by vite.config.ts). Not emoji: Windows has none, and every device drew them
 * differently.
 */
const flagUrl = (code: string) => `/flags/${code.toLowerCase()}.svg`;

export function PhoneField({
  label,
  value,
  onChange,
  showError,
  optional = false,
}: {
  label: string;
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  /** Show the problem now — on a submit attempt — rather than after leaving the field. */
  showError: boolean;
  optional?: boolean;
}) {
  const id = useId();
  const [ready, setReady] = useState(lib !== null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!lib) void loadLib().then(() => setReady(true));
  }, []);

  const countries = useMemo(() => {
    if (!ready || !lib) return [{ code: value.country, dial: '', name: value.country }];
    const names = new Intl.DisplayNames([getLocale()], { type: 'region' });
    const l = lib;
    return l
      .getCountries()
      .map((code) => ({ code, dial: l.getCountryCallingCode(code), name: names.of(code) ?? code }))
      .sort((a, b) => a.name.localeCompare(b.name, getLocale()));
  }, [ready, value.country]);

  const current = countries.find((c) => c.code === value.country);
  const empty = value.number.trim() === '';
  const valid = ready && !empty && phoneE164(value) !== null;
  const problem =
    (touched || showError) && ready && !valid && !(optional && empty)
      ? empty
        ? t('phone.required')
        : t('phone.invalid', { country: current?.name ?? value.country })
      : null;

  const typeNumber = (raw: string) => {
    // Pasted or typed with its own +code: switch the country to match, so
    // "+880 1712…" is not checked as a British number.
    if (lib && raw.trim().startsWith('+')) {
      const parsed = lib.parsePhoneNumberFromString(raw);
      if (parsed?.country) {
        onChange({ country: parsed.country, number: parsed.formatNational() });
        return;
      }
    }
    onChange({ ...value, number: raw.replace(/[^\d\s()+-]/g, '') });
  };

  // Shown back in the country's own grouping once it is complete.
  const tidy = () => {
    setTouched(true);
    if (!lib) return;
    const parsed = lib.parsePhoneNumberFromString(value.number, value.country);
    if (parsed?.isValid()) onChange({ ...value, number: parsed.formatNational() });
  };

  return (
    <div className="gs-field">
      <label className="gs-field-label" htmlFor={`${id}-number`}>
        {label}
      </label>
      <div className="gs-phone" data-invalid={problem ? '' : undefined}>
        <span className="gs-phone__country">
          <img
            className="gs-phone__flag"
            src={flagUrl(value.country)}
            alt=""
            width={20}
            height={20}
            // A territory without its own flag shows no picture rather than a broken one.
            onError={(e) => (e.currentTarget.style.visibility = 'hidden')}
            onLoad={(e) => (e.currentTarget.style.visibility = '')}
          />
          <span className="gs-phone__dial" aria-hidden="true">
            {current?.dial ? `+${current.dial}` : ''}
          </span>
          <svg className="gs-phone__caret" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <select
            aria-label={t('phone.country')}
            value={value.country}
            onChange={(e) => onChange({ ...value, country: e.target.value as CountryCode })}
          >
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
                {c.dial ? ` (+${c.dial})` : ''}
              </option>
            ))}
          </select>
        </span>
        <input
          id={`${id}-number`}
          className="gs-phone__number"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={value.number}
          onChange={(e) => typeNumber(e.target.value)}
          onBlur={tidy}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? `${id}-error` : undefined}
        />
      </div>
      {problem && (
        <span id={`${id}-error`} className="gs-field-error" role="alert">
          {problem}
        </span>
      )}
    </div>
  );
}
